/**
 * AuthProvider Component
 * Handles authentication initialization on app load
 * Validates existing tokens and restores auth state
 */

import { useEffect, ReactNode } from 'react';
import { useAuthStore } from '@/store/auth.store';
import {
  getAccessToken,
  getRefreshToken,
  clearTokens,
  setTokens,
  isTokenExpired,
} from '@/utils/token';
import * as authApi from '@/api/auth.api';

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * AuthProvider
 * Wraps the application and handles auth state initialization
 *
 * On mount:
 * 1. Checks for existing tokens in storage
 * 2. If tokens exist and are valid, fetches current user
 * 3. If tokens are expired, attempts to refresh
 * 4. Sets isInitialized to true when done
 *
 * @example
 * ```tsx
 * <AuthProvider>
 *   <App />
 * </AuthProvider>
 * ```
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { setAuth, setLoading, setInitialized, logout } = useAuthStore();

  useEffect(() => {
    const initializeAuth = async () => {
      setLoading(true);

      try {
        const accessToken = getAccessToken();
        const refreshToken = getRefreshToken();

        // No tokens - user is not logged in; clear any stale persisted state
        if (!accessToken && !refreshToken) {
          logout();
          setInitialized(true);
          return;
        }

        // Check if access token is valid
        if (accessToken && !isTokenExpired(accessToken)) {
          // Token is valid, fetch current user
          try {
            const user = await authApi.getCurrentUser();
            setAuth(user, { accessToken: accessToken!, refreshToken: refreshToken! });
            setInitialized(true);
            return;
          } catch {
            // Token might be invalid on server side, try refresh
          }
        }

        // Try to refresh the token
        if (refreshToken) {
          try {
            const response = await authApi.refreshToken(refreshToken);

            // Persist new tokens so subsequent API calls use them
            setTokens(response.accessToken, response.refreshToken);

            // Fetch user with new token
            const user = await authApi.getCurrentUser();

            setAuth(user, {
              accessToken: response.accessToken,
              refreshToken: response.refreshToken,
            });
            setInitialized(true);
            return;
          } catch {
            // Refresh failed, clear everything
            clearTokens();
            logout();
          }
        }

        // No valid auth
        setInitialized(true);
      } catch (error) {
        console.error('Auth initialization error:', error);
        clearTokens();
        logout();
        setInitialized(true);
      }
    };

    initializeAuth();
  }, [setAuth, setLoading, setInitialized, logout]);

  // SessionTimeoutWarning is rendered in ProtectedLayout (inside router context)
  return <>{children}</>;
};
